/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useCallback, useMemo, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, TextField, Autocomplete, Button, CircularProgress, Chip, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Iconify } from 'src/components';
import { dispatch } from 'src/redux/store';
import { quickCreateLead, updatePersonalDetail } from 'src/redux/slices/lead';
import { updateError } from 'src/redux/slices/user';
import { callApi } from 'src/utils/apiCall';

const CALLER_TYPE_OPTIONS = [
  {
    value: 'new_patient',
    title: 'New Patient',
    subtitle: 'First time caller',
    icon: 'solar:user-plus-bold',
    color: '#00A76F',
    bgColor: '#00A76F14',
  },
  {
    value: 'existing_patient',
    title: 'Existing Patient',
    subtitle: 'Already in system',
    icon: 'solar:user-check-bold',
    color: '#006C9C',
    bgColor: '#006C9C14',
  },
];

/**
 * IdentifyCallerLive - Component for identifying the caller
 * - New Patient / Existing Patient toggle
 * - First name and last name autocomplete fields to search existing leads
 * - Create Lead button in dropdown footer
 * - Auto-create lead on navigation if details filled but not saved
 * - Mark lead as registered if existing patient selected
 */
const IdentifyCallerLive = forwardRef(({
  formData,
  onChange,
  practiceId,
  scriptCategory,
  disabled,
  onLeadCreated,
  onLeadSelected,
}, ref) => {
  const theme = useTheme();

  // Local state for autocomplete
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isCreatingLead, setIsCreatingLead] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const searchTimeoutRef = useRef(null);
  const lastSearchQueryRef = useRef('');
  const isCreatingRef = useRef(false); // Synchronous guard against double-creation
  const searchSeqRef = useRef(0); // Monotonic counter to discard stale search responses

  // Set default caller type based on script category on mount
  useEffect(() => {
    if (!formData.callerType && scriptCategory) {
      const defaultType = scriptCategory === 'OUTBOUND' ? 'existing_patient' : 'new_patient';
      onChange('callerType', defaultType);
    }
  }, [scriptCategory]);

  // Cleanup search timeout on unmount
  useEffect(() => () => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
  }, []);

  // Track if user has unsaved changes (typed but not selected/created)
  const hasUnsavedChanges = useMemo(() => {
    const hasName = (formData.callerFirstName || '').trim() || (formData.callerLastName || '').trim();
    const hasSelectedLead = !!formData.selectedLeadId;
    const hasCreatedLead = !!formData.callerLeadCreated;
    return hasName && !hasSelectedLead && !hasCreatedLead;
  }, [formData.callerFirstName, formData.callerLastName, formData.selectedLeadId, formData.callerLeadCreated]);

  // Search leads directly via API instead of shared Redux state (Priority 14 fix)
  // This prevents LeadSearchField's Redux cleanup from wiping our search results
  const handleSearch = useCallback((query) => {
    if (!query || query.length < 2 || !practiceId) {
      setSearchResults([]);
      setIsSearching(false);
      setHasSearched(false);
      lastSearchQueryRef.current = '';
      return;
    }

    // Don't search for the same query
    if (query === lastSearchQueryRef.current) {
      return;
    }

    // Debounce search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      searchSeqRef.current += 1;
      const seq = searchSeqRef.current; // Capture sequence number before await
      setIsSearching(true);
      setHasSearched(false);
      lastSearchQueryRef.current = query;
      try {
        const res = await callApi(`/api/v1/practices/${practiceId}/leads/?search=${encodeURIComponent(query)}&limit=10`);
        // Only apply results if this is still the latest request
        if (seq === searchSeqRef.current && res.status === 200) {
          setSearchResults(res.data?.results || []);
        }
      } catch (err) {
        console.error('IdentifyCallerLive search failed:', err);
      }
      if (seq === searchSeqRef.current) {
        setIsSearching(false);
        setHasSearched(true);
      }
    }, 300);
  }, [practiceId]);

  // Mark a lead as registered patient
  const markLeadAsRegistered = useCallback((lead) => {
    if (!lead || !practiceId) return;

    dispatch(updatePersonalDetail(
      { id: lead.id, is_registered_patient: true },
      practiceId,
      () => {
        dispatch(updateError({ success: true, message: 'Patient marked as registered' }));
      },
      'callScriptIdentify'
    ));
  }, [practiceId]);

  // Handle selecting a lead from autocomplete (moved before name handlers — Priority 15 fix)
  const handleSelectLead = useCallback((lead) => {
    if (!lead) return;

    onChange('callerFirstName', lead.first_name || '');
    onChange('callerLastName', lead.last_name || '');
    onChange('selectedLeadId', lead.id);
    onChange('selectedLeadData', lead);
    onChange('callerLeadCreated', false);

    // Clear search state
    setSearchResults([]);
    setHasSearched(false);

    // Check if caller type is existing_patient and lead is not registered
    const { callerType } = formData;
    if (callerType === 'existing_patient' && !lead.is_registered_patient) {
      markLeadAsRegistered(lead);
    }

    if (onLeadSelected) {
      onLeadSelected(lead);
    }
  }, [onChange, formData.callerType, onLeadSelected, markLeadAsRegistered]);

  // Handle first name change
  const handleFirstNameChange = useCallback((event, newValue, reason) => {
    if (disabled) return;

    if (reason === 'selectOption' && newValue && typeof newValue === 'object') {
      handleSelectLead(newValue);
    } else if (reason === 'input' || reason === 'clear') {
      const value = typeof newValue === 'string' ? newValue : '';
      onChange('callerFirstName', value);

      // Only clear selected lead if the name actually changed from the selected lead's name
      // This prevents clearing when user clicks on field with existing lead data
      const currentSelectedLead = formData.selectedLeadData;
      const nameChanged = !currentSelectedLead || (value.toLowerCase() !== (currentSelectedLead.first_name || '').toLowerCase());
      if (nameChanged) {
        onChange('selectedLeadId', null);
        onChange('selectedLeadData', null);
        onChange('callerLeadCreated', false);
      }

      handleSearch(value);
    }
  }, [onChange, disabled, handleSearch, formData.selectedLeadData, handleSelectLead]);

  // Handle last name change
  const handleLastNameChange = useCallback((event, newValue, reason) => {
    if (disabled) return;

    if (reason === 'selectOption' && newValue && typeof newValue === 'object') {
      handleSelectLead(newValue);
    } else if (reason === 'input' || reason === 'clear') {
      const value = typeof newValue === 'string' ? newValue : '';
      onChange('callerLastName', value);

      // Only clear selected lead if the name actually changed from the selected lead's name
      // This prevents clearing when user clicks on field with existing lead data
      const currentSelectedLead = formData.selectedLeadData;
      const nameChanged = !currentSelectedLead || (value.toLowerCase() !== (currentSelectedLead.last_name || '').toLowerCase());
      if (nameChanged) {
        onChange('selectedLeadId', null);
        onChange('selectedLeadData', null);
        onChange('callerLeadCreated', false);
      }

      handleSearch(value);
    }
  }, [onChange, disabled, handleSearch, formData.selectedLeadData, handleSelectLead]);

  // Handle caller type selection
  const handleCallerTypeSelect = useCallback((value) => {
    if (disabled) return;
    const newValue = formData.callerType === value ? '' : value;
    onChange('callerType', newValue);

    if (value === 'existing_patient' && formData.selectedLeadData && !formData.selectedLeadData.is_registered_patient) {
      markLeadAsRegistered(formData.selectedLeadData);
    }
  }, [formData.callerType, formData.selectedLeadData, onChange, disabled, markLeadAsRegistered]);

  // Create a new lead with the entered details
  const createNewLead = useCallback((successCallback) => {
    // Synchronous guard — prevents double-creation from rapid calls
    if (isCreatingRef.current) return false;

    let firstName = (formData.callerFirstName || '').trim();
    let lastName = (formData.callerLastName || '').trim();

    // If lastName is empty and firstName contains a space, split it
    if (!lastName && firstName.includes(' ')) {
      const nameParts = firstName.split(' ');
      firstName = nameParts[0];
      lastName = nameParts.slice(1).join(' ');

      // Update form data with split names
      onChange('callerFirstName', firstName);
      onChange('callerLastName', lastName);
    }

    if (!firstName && !lastName) {
      return false;
    }

    if (!practiceId) {
      console.error('No practiceId provided for lead creation');
      return false;
    }

    isCreatingRef.current = true;
    setIsCreatingLead(true);

    const leadData = {
      first_name: firstName,
      last_name: lastName,
      is_registered_patient: formData.callerType === 'existing_patient',
    };

    dispatch(quickCreateLead(
      practiceId,
      leadData,
      (createdLead) => {
        isCreatingRef.current = false;
        setIsCreatingLead(false);
        onChange('selectedLeadId', createdLead.id);
        onChange('selectedLeadData', createdLead);
        onChange('callerLeadCreated', true);

        // Clear search state
        setSearchResults([]);
        setHasSearched(false);

        if (onLeadCreated) {
          onLeadCreated(createdLead);
        }

        if (successCallback) {
          successCallback(createdLead);
        }
      },
      (error) => {
        isCreatingRef.current = false;
        setIsCreatingLead(false);
        console.error('Failed to create lead:', error);
      }
    ));

    return true;
  }, [formData.callerFirstName, formData.callerLastName, formData.callerType, practiceId, onChange, onLeadCreated]);

  // Handle Create Lead button click
  const handleCreateLead = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    createNewLead();
  }, [createNewLead]);

  // Expose method to parent to auto-create lead on navigation
  useImperativeHandle(ref, () => ({
    autoCreateLeadIfNeeded: (callback) => {
      if (hasUnsavedChanges) {
        return createNewLead(callback);
      }
      return false;
    },
    hasUnsavedChanges: () => hasUnsavedChanges,
  }), [hasUnsavedChanges, createNewLead]);

  // Get option label for autocomplete
  const getOptionLabel = useCallback((option) => {
    if (typeof option === 'string') return option;
    const parts = [option.first_name, option.last_name].filter(Boolean);
    return parts.join(' ') || '';
  }, []);

  // Render option for autocomplete dropdown
  const renderOption = useCallback((props, option) => {
    // eslint-disable-next-line react/prop-types
    const { key, ...restProps } = props;
    const fullName = getOptionLabel(option);

    // lead_contact can be object or array - handle both cases
    const contact = Array.isArray(option.lead_contact)
      ? option.lead_contact[0]
      : option.lead_contact;
    const phone = contact?.phone_number || contact?.phone || option.phone_number || option.phone || '';
    const email = contact?.email || option.email || '';

    return (
      <Box
        component="li"
        key={key}
        {...restProps}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1.25,
          px: 1.5,
          '&:hover': {
            backgroundColor: `${theme.palette.primary.main}08`,
          },
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: option.is_registered_patient ? '#00A76F14' : '#63738114',
            flexShrink: 0,
          }}
        >
          <Iconify
            icon={option.is_registered_patient ? 'solar:user-check-bold' : 'solar:user-bold'}
            sx={{ width: 20, height: 20, color: option.is_registered_patient ? '#00A76F' : '#637381' }}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
            <Typography
              noWrap
              sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              {fullName}
            </Typography>
            {option.is_registered_patient && (
              <Chip
                label="Registered"
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  backgroundColor: '#00A76F14',
                  color: '#00A76F',
                  flexShrink: 0,
                }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Iconify icon="solar:phone-bold" sx={{ width: 12, height: 12, color: theme.palette.text.disabled }} />
                <Typography sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>
                  {phone}
                </Typography>
              </Box>
            )}
            {email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Iconify icon="solar:letter-bold" sx={{ width: 12, height: 12, color: theme.palette.text.disabled }} />
                <Typography noWrap sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary, maxWidth: 180 }}>
                  {email}
                </Typography>
              </Box>
            )}
            {!phone && !email && (
              <Typography sx={{ fontSize: '0.7rem', color: theme.palette.text.disabled, fontStyle: 'italic' }}>
                No contact info
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  }, [theme, getOptionLabel]);

  // Custom Paper component with Create Lead button at bottom
  const CustomPaper = useCallback(({ children, ...paperProps }) => {
    const showCreateButton = hasUnsavedChanges && hasSearched && !isSearching;

    return (
      <Paper {...paperProps}>
        {children}
        {showCreateButton && (
          <Box
            sx={{
              p: 1,
              borderTop: `1px solid ${theme.palette.divider}`,
              position: 'sticky',
              bottom: 0,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Button
              fullWidth
              size="small"
              onMouseDown={handleCreateLead}
              disabled={disabled || isCreatingLead}
              startIcon={isCreatingLead ? <CircularProgress size={14} color="inherit" /> : <Iconify icon="solar:user-plus-bold" />}
              sx={{
                py: 1,
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                backgroundColor: '#00A76F',
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#007867',
                },
              }}
            >
              {isCreatingLead ? 'Creating...' : `Create "${`${(formData.callerFirstName || '').trim()} ${(formData.callerLastName || '').trim()}`.trim() || 'New Lead'}"`}
            </Button>
          </Box>
        )}
      </Paper>
    );
  }, [hasUnsavedChanges, hasSearched, isSearching, handleCreateLead, disabled, isCreatingLead, formData.callerFirstName, formData.callerLastName, theme]);

  // Styles
  const fieldLabelSx = {
    fontSize: '0.8rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    mb: 0.5,
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: theme.palette.background.paper,
      fontSize: '0.875rem',
      height: '40px',
      '& fieldset': {
        borderColor: formData.selectedLeadId ? theme.palette.primary.main : theme.palette.divider,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.light,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputBase-input': {
      py: 1,
      px: 1.5,
    },
  };

  // Check if we should show "selected" state
  const hasSelectedLead = !!formData.selectedLeadId;
  const selectedLead = formData.selectedLeadData;

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Caller Type Selection */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {CALLER_TYPE_OPTIONS.map((option) => {
          const isSelected = formData.callerType === option.value;
          return (
            <Box
              key={option.value}
              onClick={() => handleCallerTypeSelect(option.value)}
              sx={{
                flex: '1 1 auto',
                minWidth: 140,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                borderRadius: '12px',
                border: `1.5px solid ${isSelected ? option.color : theme.palette.divider}`,
                backgroundColor: isSelected ? option.bgColor : theme.palette.background.paper,
                cursor: disabled ? 'default' : 'pointer',
                transition: 'all 0.15s ease',
                '&:hover': disabled ? {} : {
                  borderColor: option.color,
                  backgroundColor: option.bgColor,
                },
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: option.bgColor,
                  flexShrink: 0,
                }}
              >
                <Iconify
                  icon={option.icon}
                  sx={{ width: 20, height: 20, color: option.color }}
                />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: '0.8rem',
                    fontWeight: isSelected ? 600 : 500,
                    color: isSelected ? option.color : theme.palette.text.primary,
                    lineHeight: 1.3,
                  }}
                >
                  {option.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    color: theme.palette.text.secondary,
                    lineHeight: 1.3,
                  }}
                >
                  {option.subtitle}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Selected Lead Display */}
      {hasSelectedLead && selectedLead && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            borderRadius: '12px',
            backgroundColor: '#00A76F08',
            border: '1px solid #00A76F40',
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#00A76F14',
            }}
          >
            <Iconify icon="solar:user-check-bold" sx={{ width: 22, height: 22, color: '#00A76F' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: theme.palette.text.primary }}>
                {selectedLead.first_name} {selectedLead.last_name}
              </Typography>
              {formData.callerLeadCreated && (
                <Chip
                  label="Just Created"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    backgroundColor: '#00B8D914',
                    color: '#00B8D9',
                  }}
                />
              )}
              {selectedLead.is_registered_patient && (
                <Chip
                  label="Registered"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    backgroundColor: '#00A76F14',
                    color: '#00A76F',
                  }}
                />
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>
              Lead ID: {selectedLead.id}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="text"
            onClick={() => {
              onChange('selectedLeadId', null);
              onChange('selectedLeadData', null);
              onChange('callerLeadCreated', false);
            }}
            disabled={disabled}
            sx={{
              minWidth: 'auto',
              p: 0.5,
              color: theme.palette.text.secondary,
              '&:hover': { color: theme.palette.error.main },
            }}
          >
            <Iconify icon="eva:close-fill" sx={{ width: 20, height: 20 }} />
          </Button>
        </Box>
      )}

      {/* Name Fields - Only show if no lead selected */}
      {!hasSelectedLead && (
        <>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
            {/* First Name */}
            <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 0 } }}>
              <Typography sx={fieldLabelSx}>
                <Iconify icon="solar:user-bold" sx={{ width: 14, height: 14 }} />
                First Name
              </Typography>
              <Autocomplete
                freeSolo
                options={searchResults}
                getOptionLabel={getOptionLabel}
                renderOption={renderOption}
                inputValue={formData.callerFirstName || ''}
                onInputChange={handleFirstNameChange}
                onChange={(e, value) => {
                  if (value && typeof value === 'object') {
                    handleSelectLead(value);
                  }
                }}
                disabled={disabled}
                loading={isSearching}
                filterOptions={(x) => x}
                noOptionsText={hasSearched ? "No leads found" : "Type to search..."}
                loadingText="Searching..."
                PaperComponent={CustomPaper}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Enter first name..."
                    sx={textFieldSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isSearching && <CircularProgress size={16} sx={{ mr: 1 }} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            {/* Last Name */}
            <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 0 } }}>
              <Typography sx={fieldLabelSx}>
                <Iconify icon="solar:user-bold" sx={{ width: 14, height: 14 }} />
                Last Name
              </Typography>
              <Autocomplete
                freeSolo
                options={searchResults}
                getOptionLabel={getOptionLabel}
                renderOption={renderOption}
                inputValue={formData.callerLastName || ''}
                onInputChange={handleLastNameChange}
                onChange={(e, value) => {
                  if (value && typeof value === 'object') {
                    handleSelectLead(value);
                  }
                }}
                disabled={disabled}
                loading={isSearching}
                filterOptions={(x) => x}
                noOptionsText={hasSearched ? "No leads found" : "Type to search..."}
                loadingText="Searching..."
                PaperComponent={CustomPaper}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Enter last name..."
                    sx={textFieldSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isSearching && <CircularProgress size={16} sx={{ mr: 1 }} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
});

IdentifyCallerLive.displayName = 'IdentifyCallerLive';

IdentifyCallerLive.propTypes = {
  formData: PropTypes.object,
  onChange: PropTypes.func,
  practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  scriptCategory: PropTypes.oneOf(['INBOUND', 'OUTBOUND']),
  disabled: PropTypes.bool,
  onLeadCreated: PropTypes.func,
  onLeadSelected: PropTypes.func,
};

IdentifyCallerLive.defaultProps = {
  formData: {},
  onChange: () => {},
  practiceId: null,
  scriptCategory: 'INBOUND',
  disabled: false,
  onLeadCreated: null,
  onLeadSelected: null,
};

export default IdentifyCallerLive;
